import React, { useRef } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { Swiper, SwiperSlide } from "swiper/react";

import { FreeMode, Pagination, Navigation } from "swiper";
import HighestProjectCard from "./HighestProjectCard";
import { useGetProjectsQuery } from "../../redux/slices/api/projectsApiSlice";
import { Bars } from "react-loader-spinner";
import Container from "../common/Container";

const HighestProject = () => {
  const mySlides = useRef(null);

  const { data: projectsData, isLoading } = useGetProjectsQuery();
  
  const filteredProjects = projectsData?.length
    ? [...projectsData]
        .sort((a, b) => (b?.amount_raised ?? 0) - (a?.amount_raised ?? 0))
        .slice(0, 7)
    : [];

  return (
    <Container>
      <div className="w-full flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
        {/* Left Side - Title & Description */}
        <div className="w-full lg:w-[320px] xl:w-[380px] flex-shrink-0">
          <div className="flex flex-col sticky top-24">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-primary-green/20 backdrop-blur-md border border-primary/30 text-primary-green roboto-font text-sm font-semibold w-fit mb-6">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Top Performers
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl abel-font text-white leading-[1.1] mb-6">
              Highest
              <span className="block bg-gradient-to-r from-primary via-[#a66eff] to-primary-green bg-clip-text text-transparent mt-1">
                Funded Projects
              </span>
            </h2>
            <p className="text-white/60 text-base lg:text-lg leading-relaxed mb-8">
              Discover the most successful research initiatives powered by our community.
            </p>
            
            {/* Navigation Arrows - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <button 
                id="swiper-back"
                className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-white/10 to-white/5 hover:from-primary/30 hover:to-primary/10 border border-white/10 hover:border-primary/40 transition-all duration-300 group backdrop-blur-sm"
              >
                <HiChevronLeft className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
              </button>
              <button 
                id="swiper-forward"
                className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-white/10 to-white/5 hover:from-primary/30 hover:to-primary/10 border border-white/10 hover:border-primary/40 transition-all duration-300 group backdrop-blur-sm"
              >
                <HiChevronRight className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Right Side - Carousel */}
        <div className="w-full lg:flex-1">
        
          {/* Slider Section */}
          {isLoading ? (
            <div className="w-full min-h-[450px] flex justify-center items-center rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-4">
                <Bars
                  height="50"
                  width="50"
                  color="#0EA4BD"
                  ariaLabel="bars-loading"
                  visible={true}
                />
                <span className="text-white/50 roboto-font">Loading projects...</span>
              </div>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="w-full relative">
              {/* Mobile Navigation - Show on small screens */}
              <div className="flex lg:hidden items-center justify-end gap-3 mb-6">
                <button 
                  id="swiper-back-mobile"
                  className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/20 transition-all duration-300 group"
                >
                  <HiChevronLeft className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
                </button>
                <button 
                  id="swiper-forward-mobile"
                  className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/20 transition-all duration-300 group"
                >
                  <HiChevronRight className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
                </button>
              </div>
              
              <Swiper
                ref={mySlides}
                spaceBetween={20}
                freeMode={true}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                modules={[FreeMode, Pagination, Navigation]}
                className="highest-project-swiper !pb-12"
                navigation={{ 
                  nextEl: window.innerWidth < 1024 ? "#swiper-forward-mobile" : "#swiper-forward", 
                  prevEl: window.innerWidth < 1024 ? "#swiper-back-mobile" : "#swiper-back" 
                }}
                loop={filteredProjects.length > 2}
                breakpoints={{
                  300: {
                    freeMode: false,
                    slidesPerView: 1,
                    spaceBetween: 16,
                  },
                  640: {
                    freeMode: false,
                    slidesPerView: 1.5,
                    spaceBetween: 20,
                  },
                  1024: {
                    freeMode: true,
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  1280: {
                    freeMode: true,
                    slidesPerView: 2.5,
                    spaceBetween: 24,
                  },
                  1536: {
                    freeMode: true,
                    slidesPerView: 3,
                    spaceBetween: 24,
                  },
                }}
              >
                {filteredProjects.map((item, index) => (
                  <SwiperSlide key={index}>
                    <HighestProjectCard data={item} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="w-full min-h-[400px] flex flex-col justify-center items-center rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm p-12">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary-green/20 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">No Projects Yet</h3>
              <p className="text-white/50 text-center max-w-md">
                Be the first to create a crowdfunding project and start your research journey.
              </p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default HighestProject;
